<template>
  <section class="editor-navigation-settings" aria-label="项目页面交互">
    <h2 class="editor-panel-heading">页面导航</h2>
    <p v-if="isPageNavigation" class="editor-navigation-hint">此组件按每个导航项切换页面，请到“属性”页配置页面范围、名称、顺序及样式；不叠加整层点击动作。</p>
    <p v-else-if="isReturn" class="editor-navigation-hint">按实际访问顺序返回上级，同时恢复产品/设备参数。入口页没有上级时禁用。</p>
    <template v-else>
      <el-form-item label="点击动作" for="navigation-action">
        <el-select id="navigation-action" v-model="actionType" aria-label="页面点击动作">
          <el-option value="none" label="无页面导航"></el-option>
          <el-option value="page" label="跳转项目页面"></el-option>
          <el-option v-if="isFleetList" value="product" :label="isProductList ? '按产品跳转' : '按所属产品跳转设备页面'"></el-option>
          <el-option value="back" label="返回上级"></el-option>
        </el-select>
      </el-form-item>
      <product-page-routes v-if="isFleetList && actionType === 'product'" :kind="isProductList ? 'product' : 'device'" :value="action" @input="setNavigation"></product-page-routes>
      <template v-if="actionType === 'page'">
        <el-form-item label="目标页面" for="navigation-target">
          <el-select id="navigation-target" :value="action.pageId" @input="setAction('pageId', $event)" aria-label="跳转目标页面" placeholder="请选择页面">
            <el-option v-for="(page, index) in main.pageProject.pages" :key="page.id" :value="page.id" :label="page.name + (page.id === main.activePageId ? '（当前页）' : '') + ' · ' + (index + 1)"></el-option>
          </el-select>
        </el-form-item>
        <div v-for="key in keys" :key="key" class="editor-navigation-param">
          <label :for="'navigation-source-' + key">{{ key === 'productId' ? '产品参数' : '设备参数' }} · {{ key }}</label>
          <el-select :id="'navigation-source-' + key" :value="rule(key).source" @input="setRule(key, 'source', $event)" :aria-label="key + ' 参数来源'">
            <el-option value="inherit" label="继承当前值（无值则不传）"></el-option>
            <el-option value="literal" label="固定值"></el-option>
            <el-option value="data" label="组件静态数据字段"></el-option>
            <el-option v-if="isFleetList" value="row" label="当前列表行字段"></el-option>
            <el-option value="clear" label="清除参数"></el-option>
          </el-select>
          <el-input v-if="rule(key).source === 'literal'" :value="rule(key).value" @input="setRule(key, 'value', $event)" :maxlength="256" :label="key + ' 固定值'" placeholder="输入参数值"></el-input>
          <el-input v-if="['data', 'row'].includes(rule(key).source)" :value="rule(key).field" @input="setRule(key, 'field', $event)" :label="key + (rule(key).source === 'row' ? ' 当前行字段' : ' 静态字段')" :placeholder="rule(key).source === 'row' ? '例如 row.id' : '例如 product.id'"></el-input>
        </div>
        <p class="editor-navigation-hint">{{ isFleetList ? '列表行字段从当前点击行读取，例如 row.id、row.productId。' : '静态字段从组件的 data 对象读取。' }}切换产品会清除旧设备；需要设备时请明确设置 deviceId。</p>
      </template>
      <p v-if="actionType === 'back'" class="editor-navigation-hint">返回访问栈中的上一个页面，不是固定首页。没有上级时禁用。</p>
    </template>
    <p v-if="issue" class="editor-navigation-error" role="alert">{{ issue }}。修正后再预览。</p>
    <p class="editor-navigation-hint">编辑时只选中组件，不触发跳转；右上角预览可体验。参数只存于当前查看窗口，不会下发设备命令。页面导航优先于原组件点击事件。</p>
  </section>
</template>
<script>
import { contextKeys, navigationIssue } from '@/runtime/page-navigation'
import ProductPageRoutes from './product-page-routes.vue'
export default {
  components: { ProductPageRoutes },
  inject: ['main'],
  data () { return { keys: contextKeys } },
  computed: {
    isPageNavigation () { return this.main.activeComponent.prop === 'navigation' && this.main.activeOption.mode === 'pages' },
    isReturn () { return this.main.activeComponent.prop === 'navigation' },
    isProductList () { return this.main.activeComponent.prop === 'productList' },
    isFleetList () { return ['productList', 'deviceList'].includes(this.main.activeComponent.prop) },
    action () { return this.main.activeObj.navigation || { type: 'none' } },
    actionType: {
      get () { return this.action.type || 'none' },
      set (type) {
        if (type === 'product' && this.isFleetList) {
          this.setNavigation({ ...this.action, type, routes: this.action.routes || [],
            fallback: this.action.fallback || (this.action.type === 'page' && this.action.pageId ? { type: 'page', pageId: this.action.pageId } : { type: 'none' }) })
        } else this.setAction('type', type)
      }
    },
    issue () {
      if (this.action.type === 'product' && !this.isFleetList) return '按产品跳转仅适用于产品列表或设备列表组件'
      return navigationIssue(this.action, this.main.pageProject.pages)
    }
  },
  methods: {
    setNavigation (value) { this.main.$set(this.main.activeObj, 'navigation', value) },
    setAction (key, value) {
      this.main.$set(this.main.activeObj, 'navigation', { ...this.action, [key]: value })
    },
    rule (key) { return this.action.params && this.action.params[key] || { source: 'inherit' } },
    setRule (key, field, value) {
      if (field === 'source' && value === 'row' && !this.rule(key).field) {
        const prop = this.main.activeComponent.prop
        const defaultField = prop === 'productList' && key === 'productId' ? 'row.id' : (prop === 'deviceList' && key === 'productId' ? 'row.productId' : 'row.id')
        this.setAction('params', { ...this.action.params, [key]: { ...this.rule(key), source: value, field: defaultField } })
        return
      }
      this.setAction('params', { ...this.action.params, [key]: { ...this.rule(key), [field]: value } })
    }
  }
}
</script>
